import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  OFF_BALANCE_LIABILITIES_DATE_COLUMN_INDEXES,
  OFF_BALANCE_LIABILITIES_TABLE_ARIA_LABEL,
  OFF_BALANCE_LIABILITIES_TABLE_COLUMN_WIDTHS,
  OFF_BALANCE_LIABILITIES_TABLE_HEADERS,
  OFF_BALANCE_LIABILITIES_TABLE_MIN_WIDTH,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/offBalanceLiabilities/constants.ts';
import { OffBalanceLiabilityRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/offBalanceLiabilities/OffBalanceLiabilityRow.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import type { IOffBalanceLiabilitiesSectionProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/offBalanceLiabilities/types.ts';
import { useDocumentGenerationOptions } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/hooks/useDocumentGenerationOptions.ts';
import type { TFinancialConditionFormValues } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { createOffBalanceLiability } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const OffBalanceLiabilitiesSection: React.FC<IOffBalanceLiabilitiesSectionProps> = ({
  disabled,
}) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const { fields, append, remove } = useFieldArray({ control, name: 'offBalanceLiabilities' });
  const { shortBankOptions, currencyOptions, isBanksLoading, isCurrenciesLoading } =
    useDocumentGenerationOptions();

  if (!borrowerType) {
    return null;
  }

  return (
    <Box sx={documentGenerationFormStyles.section}>
      <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
        6. Позабалансові зобов'язання
      </Typography>

      <FinancialConditionTable
        ariaLabel={OFF_BALANCE_LIABILITIES_TABLE_ARIA_LABEL}
        headers={[...OFF_BALANCE_LIABILITIES_TABLE_HEADERS]}
        showActions={fields.length > 1}
        dateColumnIndexes={[...OFF_BALANCE_LIABILITIES_DATE_COLUMN_INDEXES]}
        columnWidths={[...OFF_BALANCE_LIABILITIES_TABLE_COLUMN_WIDTHS]}
        minWidth={OFF_BALANCE_LIABILITIES_TABLE_MIN_WIDTH}
      >
        {fields.map((field, index) => (
          <OffBalanceLiabilityRow
            key={field.id}
            index={index}
            canRemove={fields.length > 1}
            disabled={disabled}
            creditorOptions={shortBankOptions}
            currencyOptions={currencyOptions}
            isBanksLoading={isBanksLoading}
            isCurrenciesLoading={isCurrenciesLoading}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>

      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createOffBalanceLiability())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        Додати зобов'язання
      </Button>
    </Box>
  );
};
